import React from 'react';
import {
  Container,
  Stack,
  Divider,
  Box,
  FormControl,
  Paper,
  Button,Typography
} from '@mui/material';
import Grid from "@mui/material/Grid2";
import { Autocomplete, Datas, AprovacoesCheckbox } from '../../../components';
import api from '../../../services/api';
import * as yup from 'yup';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Registros from './Registros';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CSVDownload } from 'react-csv';
import { removerQuebrasDeLinha } from '../../../utils/removerQuebraDeLinha';
import { tagsRel } from './static';

const Tema12 = () => {
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);
  let user = useStoreState(state => state.auth.user);

  const [empresas, setEmpresas] = React.useState([]);
  const [anoSelecionado, setAnoSelecionado] = React.useState(
    new Date().getFullYear() - 1
  );
  const [csvData, setCsvData] = React.useState([]);

  const loadEmpresas = async mount => {
    startLoading();
    if (mount.isMounted)
      try {
        let matrizes = (
          await api.http.get(
            '/empresas/' +
              (user.roleId == 'admin' ? '?all=1&matriz=1' : '?matriz=1')
          )
        ).data;
        setEmpresas(matrizes);
        stopLoading();
      } catch (error) {
        stopLoading({
          message: error.toString(),
          severity: 'error'
        });
      }
  };

  const schema = yup
    .object({
      empresa: yup
        .number()
        .positive()
        .label('Selecione a empresa')
        .required()
        .nullable()
    })
    .required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema)
  });

  const { watch } = useFormRef;

  const onChangeAno = (event, item) => {
    setAnoSelecionado(item);
  };

  const empresaId = watch('empresa');

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadEmpresas(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  function extrairDataHora(dateTimeStr) {
    const dateObj = new Date(dateTimeStr);
    const year = dateObj.getFullYear().toString();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const date = `${day}-${month}-${year}`;
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const seconds = dateObj.getSeconds().toString().padStart(2, '0');
    const time = `${hours}:${minutes}:${seconds}`;
    return date + ' ' + time;
  }
  function formatarMoeda(valor) {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  const exportDados = async () => {
    startLoading();
    try {
      const dados = (await api.http.get('/12/registros')).data;
      const dadosCsv = dados.map(data => {
        return {
          cnpj: data?.empresa?.cnpj,
          empresa: removerQuebrasDeLinha(data.empresa.nome),
          ano: data.ano,
          produto: removerQuebrasDeLinha(data?.produto?.name),
          producaoTotal: data?.producaoTotal,
          totalReciclados: data?.totalReciclados,
          totalOutroDest: data?.totalOutroDest,
          geracaoRejeitos: data?.geracaoRejeitos,
          geracaoEsteril: data?.geracaoEsteril,
          investEconoCircul: data?.investEconoCircul
            ? formatarMoeda(data.investEconoCircul)
            : null,
          metalurConsumCirc: data?.metalurConsumCirc,
          observacoes: removerQuebrasDeLinha(data?.observacoes),
          createdAt: data.createdAt ? extrairDataHora(data.createdAt) : null,
          updatedAt: data.updatedAt ? extrairDataHora(data.updatedAt) : null
        };
      });

      if (dadosCsv.length == 0) {
        stopLoading({
          message: 'Nenhum registro encontrado.',
          severity: 'error'
        });
      } else {
        setCsvData([tagsRel, ...dadosCsv]);
        stopLoading();
      }
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  };

  return (
    <Container>
      <Paper
        variant="outlined"
        className="center-box"
        sx={{
          textAlign: 'center',
          px: 4,
          pt: 2,
          pb: 3,
          m: 5
        }}
      >
        <Grid m={3}>
          <Typography>
            Acesse o seguinte link para referências sobre o preenchimento deste tema:
            <a
              href="https://ibram.org.br/wp-content/uploads/2024/09/IBRAM_Praticas-em-Circularidade-no-Setor-Mineral_2_edicao.pdf"
              target="_blank"
              style={{ fontWeight: 'bold', color: '#004071', marginLeft: 5 }}
              rel="noreferrer"
            >
              Práticas em Circularidade no Setor Mineral.
            </a>
          </Typography>
        </Grid>
        <Divider>
          <h3>Dados básicos</h3>
        </Divider>
        <Box component="form" autoComplete="off">
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <Autocomplete
                field="empresa"
                useFormRef={useFormRef}
                options={empresas}
                getOptionLabel={empresa =>
                  empresa.nome + ' (' + empresa.cnpj + ')'
                }
                schema={schema}
              />
            </FormControl>
            <Datas onChange={onChangeAno} value={anoSelecionado} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <AprovacoesCheckbox />
          </Stack>
          {['admin', 'gestor', 'preenchedorSso'].includes(user.roleId) && (
            <Stack direction="row" spacing={2} mt={2}>
              <Button
                variant="contained"
                onClick={exportDados}
                sx={{ height: 45 }}
              >
                <FileDownloadIcon /> Exportar Dados (CSV)
              </Button>
              {csvData && csvData.length > 0 && (
                <CSVDownload
                  data={csvData}
                  headers={tagsRel.headers}
                  separator=";"
                  target="_blank"
                />
              )}
            </Stack>
          )}
        </Box>
      </Paper>

      {empresaId && (
        <Paper
          variant="outlined"
          className="preenchimento-grid center-box"
          sx={{
            textAlign: 'center',
            px: 4,
            pt: 2,
            pb: 3,
            m: 5
          }}
        >
          <Registros ano={anoSelecionado} empresa={empresaId} />
        </Paper>
      )}
    </Container>
  );
};

export default Tema12;
